import { useEffect, useMemo, useState } from "react";
import { Calendar, Clock, CheckCircle2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";

function nextDays(count = 7) {
    const out = [];
    const base = new Date();
    // Start from tomorrow (same-day often has no eligible slots this late in the day).
    for (let i = 1; i <= count; i++) {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        out.push({
            iso,
            weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
            day: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
            jsDay: d.getDay(), // 0=Sunday
        });
    }
    return out;
}

export default function AppointmentPicker({ leadId, defaultName = "", defaultPhone = "", defaultEmail = "" }) {
    const days = useMemo(() => nextDays(7), []);
    // Default to the first weekday that isn't Sunday.
    const [date, setDate] = useState(() => days.find((d) => d.jsDay !== 0)?.iso || days[0].iso);
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [booking, setBooking] = useState(false);
    const [booked, setBooked] = useState(null); // {label, slot_iso}
    const [notes, setNotes] = useState("");

    useEffect(() => {
        let active = true;
        setLoading(true);
        setSelected(null);
        http.get("/appointments/slots", { params: { date } })
            .then(({ data }) => { if (active) setSlots(data.slots || []); })
            .catch(() => { if (active) setSlots([]); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [date]);

    const confirmBooking = async () => {
        if (!selected) return;
        setBooking(true);
        try {
            const { data } = await http.post("/appointments", {
                lead_id: leadId || null,
                name: defaultName || "Lead",
                phone: defaultPhone || "",
                email: defaultEmail || null,
                slot_iso: selected.iso,
                notes: notes || null,
            });
            setBooked({ label: data.slot_label, iso: data.slot_iso });
            toast.success(`Booked! ${data.slot_label}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBooking(false);
        }
    };

    if (booked) {
        return (
            <div className="card-surface p-6 text-left" data-testid="appt-booked">
                <div className="flex items-start gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 shrink-0">
                        <CheckCircle2 className="w-5 h-5" />
                    </span>
                    <div>
                        <div className="text-sm text-emerald-300 mono uppercase tracking-widest">Call booked</div>
                        <div className="mt-1 text-xl font-semibold text-white">{booked.label}</div>
                        <p className="mt-2 text-sm text-slate-400">
                            Jeffrey will call <strong>{defaultPhone}</strong> at this time. Add it to your calendar now so you don't miss it.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card-surface p-6 text-left" data-testid="appointment-picker">
            <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 shrink-0">
                    <Calendar className="w-5 h-5" />
                </span>
                <div>
                    <div className="text-xs mono uppercase tracking-widest text-emerald-300">10-min phone call</div>
                    <h3 className="mt-1 text-xl font-semibold text-white">Lock in a time with Jeffrey</h3>
                    <p className="mt-1 text-sm text-slate-400">Pick any open slot in the next week. Jeffrey calls your number right on time.</p>
                </div>
            </div>

            {/* Day tabs */}
            <div className="mt-5 flex flex-wrap gap-2" data-testid="appt-day-tabs">
                {days.map((d) => (
                    <button
                        key={d.iso}
                        type="button"
                        data-testid={`appt-day-${d.iso}`}
                        onClick={() => setDate(d.iso)}
                        disabled={d.jsDay === 0}
                        className={`px-3 py-2 rounded-xl border text-xs sm:text-sm transition ${
                            d.iso === date
                                ? "bg-emerald-500/15 border-emerald-500 text-emerald-200"
                                : d.jsDay === 0
                                ? "border-white/5 text-slate-600 cursor-not-allowed"
                                : "border-white/10 text-slate-300 hover:border-emerald-500/40"
                        }`}
                    >
                        <div className="font-semibold">{d.weekday}</div>
                        <div className="text-[11px] text-slate-400">{d.day}</div>
                    </button>
                ))}
            </div>

            {/* Slots */}
            <div className="mt-5">
                {loading && <div className="text-sm text-slate-400">Loading open times…</div>}
                {!loading && slots.length === 0 && (
                    <div className="text-sm text-slate-400">
                        No open times on this day. Pick another day — or just <a href="sms:7132052971" className="text-emerald-300 underline">text Jeffrey directly</a>.
                    </div>
                )}
                {!loading && slots.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="appt-slots">
                        {slots.map((s) => (
                            <button
                                key={s.iso}
                                type="button"
                                data-testid={`appt-slot-${s.iso}`}
                                onClick={() => setSelected(s)}
                                className={`px-2 py-2 rounded-lg border text-xs sm:text-sm transition ${
                                    selected?.iso === s.iso
                                        ? "bg-emerald-500 border-emerald-500 text-emerald-950 font-bold"
                                        : "border-white/10 text-slate-200 hover:border-emerald-500/40 bg-white/[0.02]"
                                }`}
                            >
                                {s.time}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {selected && (
                <div className="mt-5 border-t border-white/5 pt-5">
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Clock className="w-4 h-4 text-emerald-300" />
                        Confirming <strong className="text-white ml-1">{selected.label}</strong>
                    </div>
                    <textarea
                        data-testid="appt-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Anything Jeffrey should know before the call? (optional)"
                        className="input-dark mt-3 min-h-[70px] text-sm"
                    />
                    <button
                        type="button"
                        onClick={confirmBooking}
                        disabled={booking}
                        data-testid="appt-confirm-btn"
                        className="btn-primary mt-4 disabled:opacity-50"
                    >
                        {booking ? "Booking…" : "Confirm 10-min call"}
                        {!booking && <ChevronRight className="w-4 h-4" />}
                    </button>
                </div>
            )}
        </div>
    );
}
